import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import DataGrid, {
    DataGridRef,
    Column,
    Toolbar, Item as ToolbarItem,
    HeaderFilter,
    FilterRow,
    Lookup, Button as DxButton
} from 'devextreme-react/data-grid';
import { Popup as DxPopup, ToolbarItem as PopupToolbarItem } from 'devextreme-react/popup';
import DateBox from 'devextreme-react/date-box';
import SelectBox from 'devextreme-react/select-box';
import CheckBox from 'devextreme-react/check-box';
import NumberBox from 'devextreme-react/number-box';
import TextArea from 'devextreme-react/text-area';
import { WorkOrderPhotos } from '../work-order-photos/WorkOrderPhotos';
import { withLoadPanel } from '../../../utils/withLoadPanel';
import './WorkOrderTable.scss';
import DataSource from 'devextreme/data/data_source';
import Guid from 'devextreme/core/guid';
import 'devextreme/data/odata/store';
import axios from 'axios';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../../contexts/auth';
import moment from 'moment';
import ODataStore from 'devextreme/data/odata/store';
import { confirm } from 'devextreme/ui/dialog';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const toOidString = (oid: any): string | undefined => {
  if (!oid) return undefined;
  if (typeof oid === 'string') return oid;
  return oid._value?.toString() ?? oid.toString();
};

/* ------------------------------------------------------------------ */
/*  WorkOrderPopup - self-contained form (no DataGrid editing)        */
/* ------------------------------------------------------------------ */

interface FormState {
  WorkOrderDate: Date | null;
  Done: boolean;
  CreatedByOid: any;
  DrovenKm: number;
  ArrivalTime: Date | null;
  DepartureTime: Date | null;
  PerformedTasks: string;
}

interface MaterialItem {
  Name: string;
  Quantity: number;
}

const emptyForm = (): FormState => ({
  WorkOrderDate: new Date(),
  Done: false,
  CreatedByOid: null,
  DrovenKm: 0,
  ArrivalTime: null,
  DepartureTime: null,
  PerformedTasks: '',
});

const WorkOrderPopup = React.memo(({
  visible,
  editData,
  workTaskOid,
  token,
  employeesDataSource,
  onClose,
  onSaved,
}: {
  visible: boolean;
  editData: any | null;
  workTaskOid: string;
  token: string | undefined;
  employeesDataSource: ODataStore;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);

  /* Initialise state every time the popup opens */
  useEffect(() => {
    if (!visible) return;
    if (editData) {
      setForm({
        WorkOrderDate: editData.WorkOrderDate ? new Date(editData.WorkOrderDate) : new Date(),
        Done: editData.Done ?? false,
        CreatedByOid: editData.CreatedBy?.Oid ?? null,
        DrovenKm: editData.DrovenKm ?? 0,
        ArrivalTime: editData.ArrivalTime ?? null,
        DepartureTime: editData.DepartureTime ?? null,
        PerformedTasks: editData.PerformedTasks ?? '',
      });
      setMaterials(
        (editData.UsedMaterials || []).map((m: any) => ({ Name: m.Name, Quantity: m.Quantity }))
      );
    } else {
      setForm(emptyForm());
      setMaterials([]);
    }
  }, [visible, editData]);

  /* Field helpers */
  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  /* Materials helpers */
  const addMaterial = useCallback(() => {
    setMaterials(prev => [...prev, { Name: '', Quantity: 1 }]);
  }, []);

  const removeMaterial = useCallback((index: number) => {
    setMaterials(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateMaterial = useCallback((index: number, field: string, value: any) => {
    setMaterials(prev => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  }, []);

  /* Save */
  const handleSave = useCallback(() => {
    if (!form.WorkOrderDate) { notify('Uitvoerdatum ontbreekt!', 'warning', 2000); return; }
    if (!form.ArrivalTime) { notify('Aankomst tijd ontbreekt!', 'warning', 2000); return; }
    if (!form.DepartureTime) { notify('Vertrek tijd ontbreekt!', 'warning', 2000); return; }
    if (!form.CreatedByOid) { notify('Selecteer een medewerker!', 'warning', 2000); return; }

    const payload = {
      WorkOrderDate: form.WorkOrderDate,
      PerformedTasks: form.PerformedTasks,
      DrovenKm: form.DrovenKm ?? 0,
      Done: form.Done ?? false,
      WorkTaskOid: workTaskOid,
      CreatedByOid: toOidString(form.CreatedByOid),
      ArrivalTimeString: form.ArrivalTime ? moment(form.ArrivalTime).format('HH:mm') : undefined,
      DepartureTimeString: form.DepartureTime ? moment(form.DepartureTime).format('HH:mm') : undefined,
      UsedMaterials: materials
        .filter(m => m.Name?.trim())
        .map(m => ({ Name: m.Name, Quantity: m.Quantity })),
    };

    const headers = { Authorization: `Bearer ${token}` };
    const request = editData
      ? axios.put(`/api/WorkOrder/Update/${toOidString(editData.Oid)}`, payload, { headers })
      : axios.post('/api/WorkOrder/Create', payload, { headers });

    request
      .then(() => { onSaved(); onClose(); })
      .catch(() => notify('Er is een fout opgetreden bij het opslaan van de werkbon', 'error', 2000));
  }, [form, materials, editData, workTaskOid, token, onSaved, onClose]);

  /* Toolbar button options (memoised to avoid popup re-renders) */
  const saveOptions = useMemo(
    () => ({ text: 'Opslaan', type: 'default', stylingMode: 'contained', onClick: handleSave }),
    [handleSave]
  );
  const cancelOptions = useMemo(
    () => ({ text: 'Annuleren', onClick: onClose }),
    [onClose]
  );

  return (
    <DxPopup
      visible={visible}
      onHiding={onClose}
      dragEnabled={false}
      hideOnOutsideClick={false}
      showTitle
      title={editData ? 'Werkbon bewerken' : 'Werkbon toevoegen'}
      width={700}
      height={650}>
      <PopupToolbarItem widget='dxButton' toolbar='bottom' location='after' options={saveOptions} />
      <PopupToolbarItem widget='dxButton' toolbar='bottom' location='after' options={cancelOptions} />

      <div className='workorder-popup-form'>
        <div className='form-grid'>
          <div className='form-field'>
            <label>Uitvoerdatum *</label>
            <DateBox
              value={form.WorkOrderDate}
              onValueChanged={e => updateField('WorkOrderDate', e.value)}
              type='date'
              displayFormat='dd-MM-yyyy'
            />
          </div>

          <div className='form-field'>
            <label>Factureerd</label>
            <CheckBox
              value={form.Done}
              onValueChanged={e => updateField('Done', e.value)}
            />
          </div>

          <div className='form-field'>
            <label>Uitgevoerd door *</label>
            <SelectBox
              dataSource={employeesDataSource}
              valueExpr='Oid'
              displayExpr='FullName'
              value={form.CreatedByOid}
              onValueChanged={e => updateField('CreatedByOid', e.value)}
              searchEnabled
              placeholder='Selecteer medewerker...'
            />
          </div>

          <div className='form-field'>
            <label>Gereden km</label>
            <NumberBox
              value={form.DrovenKm}
              onValueChanged={e => updateField('DrovenKm', e.value)}
              min={0}
            />
          </div>

          <div className='form-field'>
            <label>Aankomst tijd *</label>
            <DateBox
              value={form.ArrivalTime}
              onValueChanged={e => updateField('ArrivalTime', e.value)}
              type='time'
              displayFormat='HH:mm'
            />
          </div>

          <div className='form-field'>
            <label>Vertrek tijd *</label>
            <DateBox
              value={form.DepartureTime}
              onValueChanged={e => updateField('DepartureTime', e.value)}
              type='time'
              displayFormat='HH:mm'
            />
          </div>

          <div className='form-field form-field-full'>
            <label>Uitgevoerde werkzaamheden</label>
            <TextArea
              value={form.PerformedTasks}
              onValueChanged={e => updateField('PerformedTasks', e.value)}
              height={150}
            />
          </div>
        </div>

        <div className='materials-section'>
          <label className='materials-label'>Materiaal</label>
          <div className='materials-editor'>
            {materials.map((m, idx) => (
              <div key={idx} className='material-row'>
                <input
                  className='material-input material-name'
                  value={m.Name || ''}
                  onChange={e => updateMaterial(idx, 'Name', e.target.value)}
                  placeholder='Naam'
                />
                <input
                  className='material-input material-qty'
                  type='number'
                  value={m.Quantity ?? 1}
                  onChange={e => updateMaterial(idx, 'Quantity', parseInt(e.target.value) || 0)}
                  min={1}
                  placeholder='Aantal'
                />
                <button className='material-remove-btn' type='button' onClick={() => removeMaterial(idx)}>
                  <i className='dx-icon dx-icon-trash' />
                </button>
              </div>
            ))}
            <button className='material-add-btn' type='button' onClick={addMaterial}>
              <i className='dx-icon dx-icon-add' />
              <span>Materiaal toevoegen</span>
            </button>
          </div>
        </div>
      </div>
    </DxPopup>
  );
});

/* ------------------------------------------------------------------ */
/*  Grid                                                              */
/* ------------------------------------------------------------------ */

const Grid = ({ workTaskOid }: { workTaskOid: string }) => {
  const { user } = useAuth();
  const gridRef = useRef<DataGridRef>(null);

  /* Edit popup state */
  const [popupVisible, setPopupVisible] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  /* Photos popup state */
  const [photosPopupOpen, setPhotosPopupOpen] = useState(false);
  const [photosWorkOrderOid, setPhotosWorkOrderOid] = useState<string | null>(null);

  const openPhotos = useCallback((oid: string) => {
    setPhotosWorkOrderOid(oid);
    setPhotosPopupOpen(true);
  }, []);
  const closePhotosPopup = useCallback(() => setPhotosPopupOpen(false), []);
  const closePhotosOptions = useMemo(
    () => ({ text: 'Sluiten', onClick: closePhotosPopup }),
    [closePhotosPopup]
  );

  /* Data sources */
  const dataSource = useMemo(
    () =>
      new DataSource({
        store: {
          type: 'odata',
          version: 4,
          key: 'Oid',
          keyType: 'Guid',
          url: `${process.env.REACT_APP_URL}/api/odata/WorkOrder`,
          beforeSend: (e) => {
            e.headers = { Authorization: `Bearer ${user?.token}` };
          },
          onLoaded(result) {
            result.forEach((v) => {
              v.ArrivalTime = moment(v.ArrivalTime, 'HH:mm').toDate();
              v.DepartureTime = moment(v.DepartureTime, 'HH:mm').toDate();
            });
          },
        },
        paginate: false,
        select: [
          'Oid',
          'WorkOrderDate',
          'HasUsedMaterials',
          'PerformedTasks',
          'Done',
          'ArrivalTime',
          'DepartureTime',
          'WorkedHours',
          'CreatedBy',
          'CreatedAt',
          'UpdatedAt',
          'DrovenKm',
          'WorkTask',
        ],
        filter: ['WorkTask/Oid', '=', new Guid(workTaskOid)],
        expand: ['WorkTask', 'CreatedBy', 'UsedMaterials'],
      }),
    [user?.token, workTaskOid]
  );

  const employeesDataSource = useMemo(
    () =>
      new ODataStore({
        key: 'Oid',
        keyType: 'Guid',
        url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
        version: 4,
        beforeSend(e) {
          e.headers = { Authorization: `Bearer ${user?.token}` };
        },
      }),
    [user?.token]
  );

  /* Grid helpers */
  const refreshGrid = useCallback(() => {
    gridRef.current?.instance()?.refresh();
  }, []);

  const openNew = useCallback(() => {
    setEditData(null);
    setPopupVisible(true);
  }, []);

  const openEdit = useCallback((data: any) => {
    setEditData(data);
    setPopupVisible(true);
  }, []);

  const closePopup = useCallback(() => setPopupVisible(false), []);

  const handleDelete = useCallback(
    async(data: any) => {
      const confirmed = await confirm('Weet u zeker dat u deze werkbon wilt verwijderen?', 'Werkbon verwijderen');
      if (!confirmed) return;
      const key = toOidString(data.Oid);
      axios
        .delete(`/api/WorkOrder/${key}`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        })
        .then(() => {
          refreshGrid();
          notify('Werkbon verwijderd', 'success', 1000);
        })
        .catch(() =>
          notify('Er is een fout opgetreden bij het verwijderen van de werkbon', 'error', 1000)
        );
    },
    [user?.token, refreshGrid]
  );

  const addButtonOptions = useMemo(() => ({ icon: 'add', onClick: openNew }), [openNew]);

  return (
    <>
      <DataGrid
        ref={gridRef}
        className='tasks-grid'
        dataSource={dataSource}
        columnAutoWidth>
        <Toolbar>
          <ToolbarItem location='after' widget='dxButton' options={addButtonOptions} />
        </Toolbar>
        <HeaderFilter visible allowSearch />
        <FilterRow visible />
        <Column
          dataField='WorkOrderDate'
          sortOrder='desc'
          format='dd-MM-yyyy'
          dataType='date'
          caption='Uitvoerdatum'
        />
        <Column dataField='ArrivalTime' dataType='datetime' format='HH:mm' caption='Aankomst tijd' />
        <Column dataField='DepartureTime' dataType='datetime' format='HH:mm' caption='Vertrek tijd' />
        <Column dataField='CreatedBy.Oid' caption='Medewerker'>
          <Lookup dataSource={employeesDataSource} valueExpr='Oid' displayExpr='FullName' />
        </Column>
        <Column dataField='WorkedHours' caption='Gewerkte uren' />
        <Column caption='Materiaal' dataField='HasUsedMaterials' />
        <Column caption='Factureerd' dataField='Done' />
        <Column type='buttons' caption='Acties' width={140}>
          <DxButton hint='Bewerken' icon='edit' onClick={(e) => openEdit(e.row?.data)} />
          <DxButton hint='Verwijderen' icon='trash' onClick={(e) => handleDelete(e.row?.data)} />
          <DxButton hint="Foto's" icon='image' onClick={(e) => openPhotos(e.row?.data?.Oid)} />
        </Column>
      </DataGrid>

      <WorkOrderPopup
        visible={popupVisible}
        editData={editData}
        workTaskOid={workTaskOid}
        token={user?.token}
        employeesDataSource={employeesDataSource}
        onClose={closePopup}
        onSaved={refreshGrid}
      />

      <DxPopup
        visible={photosPopupOpen}
        onHiding={closePhotosPopup}
        dragEnabled={false}
        hideOnOutsideClick
        showTitle
        title="Foto's"
        width={800}
        height={600}>
        <PopupToolbarItem
          widget='dxButton'
          toolbar='bottom'
          location='center'
          options={closePhotosOptions}
        />
        {photosWorkOrderOid && <WorkOrderPhotos workOrderOid={photosWorkOrderOid} />}
      </DxPopup>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Exports                                                           */
/* ------------------------------------------------------------------ */

const GridWithLoadPanel = withLoadPanel(Grid);

export const WorkOrderTable = ({
  workTaskOid,
  isLoading,
}: {
  workTaskOid?: string;
  isLoading: boolean;
}) => {
  return (
    <div className='card-tasks'>
      <GridWithLoadPanel
        workTaskOid={workTaskOid}
        hasData={!!workTaskOid}
        loading={isLoading}
        panelProps={{
          container: '.card-tasks',
          position: { of: '.card-tasks' },
        }}
      />
    </div>
  );
};
